import { REVERT_CASE_TO_NEW_MODAL } from 'shared/constants/modals'
import { closeModal } from 'shared/store/modal'
import { getModalState } from 'shared/store/modal/selectors'
import { emptySplitApi, RootState } from 'shared/store/store'
import {
  BoardCaseStatusEnum,
  FinishCaseStatusEnum,
} from '../../../models/BoardCase'
import {
  IAutoSavePayload,
  IMeetingAgendaModel,
  INewDecisionRequestModel,
  MeetingCaseVoteEnum,
  MeetingStatusEnum,
} from '../../../models/BoardMeeting'
import {
  createNewCaseOrder,
  handleDropList,
  updateOrderOnAgendaItems,
} from '../../../utils/handleAgendaOrder'
import { getBoardMeeting, SystemState } from '../../meeting/selectors'
import { getAllAgendaCases, getMeetingAgendaCaseById } from './selector'
import { IDrop } from '@bbl-digital/snorre/lib/core/BoxedTable/models'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'
import {
  addOne as addOneToProposed,
  removeMany as removeManyFromProposed,
} from '../proposed'
import {
  updateMany as updateManyOnAgenda,
  updateOne as updateOneOnAgenda,
  removeOne as removeOneFromAgenda,
  addCaseError,
  setFocusedCaseId,
} from '.'
import { removeCaseErrorIfExists } from '../../../utils/handleManageServiceActions'
import { addToasterMessage } from 'shared/store/toaster'

const headers = {
  'x-api-version': '1.0',
}

interface IOnQueryStartedError {
  error: string
  isUnhandledError: boolean
}

type ServiceState = RootState & SystemState

const agendaService = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch agenda, agenda extra reducer handles loading and errors
    fetchAgenda: builder.query<IMeetingAgendaModel[], string>({
      keepUnusedDataFor: 0,
      query: (meetingId) => ({
        url: `boardmeetings/meetings/${meetingId}/agenda`,
        method: 'GET',
        headers,
      }),
    }),

    // Remove one from agenda
    removeFromAgenda: builder.mutation<
      void,
      { meetingId: string; caseId: string }
    >({
      query: ({ meetingId, caseId }) => ({
        url: `boardmeetings/meetings/${meetingId}/agenda/${caseId}`,
        method: 'DELETE',
        headers,
      }),
      async onQueryStarted({ caseId }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as ServiceState
        const item = getMeetingAgendaCaseById(caseId)(state)
        const modal = getModalState(state)

        removeCaseErrorIfExists(caseId, { getState, dispatch })

        try {
          await queryFulfilled

          dispatch(removeOneFromAgenda(caseId))
          if (item) {
            dispatch(addOneToProposed(item))
          }

          // Close modal if its the revert case to new
          if (modal.type === REVERT_CASE_TO_NEW_MODAL) {
            dispatch(closeModal())
          }
        } catch (e) {
          const err = e as IOnQueryStartedError
          if (modal.type === REVERT_CASE_TO_NEW_MODAL) return

          const error =
            axiosErrorMessageHelper(err.error) ||
            'Det skjedde en feil når du skulle ta bort sak fra agendaen'

          dispatch(addCaseError({ id: caseId, message: error }))
        }
      },
    }),

    // addCasesToAgendaFromPropsed
    addCasesToAgendaFromPropsed: builder.mutation<
      IMeetingAgendaModel[],
      IMeetingAgendaModel[]
    >({
      async queryFn(items, { getState, dispatch }, _, baseQuery) {
        const store = getState() as ServiceState
        const agenda = getAllAgendaCases(store)
        const meeting = getBoardMeeting(store)
        const meetingId = meeting?.data.id

        const managedAgendaArray = [...agenda, ...items]
        const updateWith = createNewCaseOrder(managedAgendaArray)

        const response = await baseQuery({
          url: `boardmeetings/meetings/${meetingId}/agenda`,
          method: 'PUT',
          data: { agenda: updateWith },
          headers,
        })

        if (response.error) {
          const err = axiosErrorMessageHelper(response.error, {
            fallbackError: 'Det skjedde en feil når du la til sak i agendaen',
          }) as string

          dispatch(
            addToasterMessage({
              text: err,
              type: 'danger',
            })
          )

          return { error: axiosErrorMessageHelper(response.error) }
        }

        const updatedWithCorrentOrder = updateOrderOnAgendaItems(
          managedAgendaArray,
          updateWith
        )

        return { data: updatedWithCorrentOrder }
      },

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        const removeCasesId = arg.map(({ caseId }) => caseId)
        dispatch(removeManyFromProposed(removeCasesId))
        dispatch(updateManyOnAgenda(data))
      },
    }),

    // Drop
    handleDrop: builder.mutation<IMeetingAgendaModel[], IDrop>({
      async queryFn(arg, { dispatch, getState }, _, baseQuery) {
        const store = getState() as ServiceState
        const agenda = getAllAgendaCases(store)

        const meeting = getBoardMeeting(store)
        const meetingId = meeting?.data.id

        const updated = handleDropList(agenda, arg.from, arg.to)
        const updateWith = createNewCaseOrder(updated)
        const updatedWithCorrentOrder = updateOrderOnAgendaItems(
          updated,
          updateWith
        )

        dispatch(updateManyOnAgenda(updatedWithCorrentOrder))

        const response = await baseQuery({
          url: `boardmeetings/meetings/${meetingId}/agenda`,
          method: 'PUT',
          data: { agenda: updateWith },
          headers,
        })

        if (response.error) {
          // If we fail to save, revert back to the old agenda.
          dispatch(updateManyOnAgenda(agenda))
          return { error: axiosErrorMessageHelper(response.error) }
        }

        return { data: updatedWithCorrentOrder }
      },
    }),

    // sendVotesAndDecisionForAgendaCase
    sendDecisionForAgendaCase: builder.mutation<
      IMeetingAgendaModel,
      INewDecisionRequestModel
    >({
      async queryFn(arg, { getState }, _, baseQuery) {
        const store = getState() as ServiceState
        const meeting = getBoardMeeting(store)

        if (!meeting?.data.id) return { error: 'Finnes ikke møte id.' }

        const { caseId, decisionStatus } = arg

        // Only accept votes if we have a "Til oppfølging" or "Avsluttet" case.
        const hasDecisionVotes =
          decisionStatus === FinishCaseStatusEnum.Complete ||
          decisionStatus === FinishCaseStatusEnum.Followup

        const payloadHandled: INewDecisionRequestModel = {
          ...arg,
          votes: hasDecisionVotes ? arg.votes : null,
        }

        const resUpdate = await baseQuery({
          url: `boardmeetings/meetings/${meeting.data.id}/cases/${caseId}/decision`,
          method: 'PUT',
          data: payloadHandled,
          headers,
        })

        if (resUpdate.error) {
          const message =
            axiosErrorMessageHelper(resUpdate.error) ||
            'Det skjedde en feil ved sending av vedtak'

          return { error: message }
        }

        const result = await baseQuery({
          url: `boardmeetings/meetings/${meeting.data.id}/agenda/${caseId}`,
          method: 'GET',
          headers,
        })

        if (result.error) {
          return { error: axiosErrorMessageHelper(result.error) }
        }

        return { data: result.data as IMeetingAgendaModel }
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        removeCaseErrorIfExists(arg.caseId, { getState, dispatch })

        try {
          const { data } = await queryFulfilled
          dispatch(updateOneOnAgenda(data))
          dispatch(closeModal())

          const store = getState() as ServiceState
          const agenda = getAllAgendaCases(store)

          const index = agenda.findIndex((item) => item.caseId === arg.caseId)
          const nextItem = agenda[index + 1]
          if (nextItem) {
            dispatch(setFocusedCaseId(nextItem.caseId))
          }
        } catch (e) {
          const err = e as IOnQueryStartedError
          dispatch(addCaseError({ id: arg.caseId, message: err.error }))
        }
      },
    }),

    // Revert case
    revertCase: builder.mutation<void, string>({
      async queryFn(caseId, { getState }, _, baseQuery) {
        const store = getState() as ServiceState
        const meeting = getBoardMeeting(store)

        if (!meeting?.data.id) return { error: 'Finner ikke møte id.' }

        const result = await baseQuery({
          url: `boardmeetings/meetings/${meeting.data.id}/cases/${caseId}/decision/reverted`,
          method: 'POST',
          headers,
          data: null,
        })

        if (result.error) {
          const message =
            axiosErrorMessageHelper(result.error) ||
            'Det skjedde en feil ved oppheving av beslutning'

          return { error: message }
        }

        return { data: undefined }
      },

      async onQueryStarted(arg, { queryFulfilled, getState, dispatch }) {
        removeCaseErrorIfExists(arg, { getState, dispatch })

        try {
          await queryFulfilled
          const store = getState() as ServiceState
          const meeting = getBoardMeeting(store)
          const agendaCase = getMeetingAgendaCaseById(arg)(store)

          if (agendaCase) {
            const updated: IMeetingAgendaModel = {
              ...agendaCase,
              votingStatus: MeetingCaseVoteEnum.NotVoted,
              status:
                meeting.data.status === MeetingStatusEnum.Ongoing
                  ? BoardCaseStatusEnum.Ongoing
                  : BoardCaseStatusEnum.New,
            }

            dispatch(updateOneOnAgenda(updated))
          }

          dispatch(closeModal())
        } catch (e) {
          const err = e as IOnQueryStartedError
          dispatch(addCaseError({ id: arg, message: err.error }))
        }
      },
    }),

    // Autosave
    autoSaveCase: builder.mutation<void, IAutoSavePayload>({
      async queryFn(arg, { getState, dispatch }, _, baseQuery) {
        if (arg.manualUpdate)
          removeCaseErrorIfExists(arg.caseId, { getState, dispatch })

        const store = getState() as ServiceState
        const meeting = getBoardMeeting(store)

        if (!meeting?.data.id) return { error: 'Finnes ikke møte id' }

        const result = await baseQuery({
          url: `boardmeetings/meetings/${meeting.data.id}/cases/${arg.caseId}/decision/text`,
          method: 'PUT',
          data: { decision: arg.text },
          headers,
        })

        if (result.error) {
          const err = axiosErrorMessageHelper(result.error)

          if (arg?.manualUpdate) {
            dispatch(
              addCaseError({
                id: arg.caseId,
                message: err || 'Det skjedde en feil ved oppdatering av sak',
              })
            )
          }

          return { error: err }
        }

        return { data: undefined }
      },
    }),
  }),
})

export const {
  // Mutations
  useRemoveFromAgendaMutation,
  useAddCasesToAgendaFromPropsedMutation,
  useHandleDropMutation,
  useSendDecisionForAgendaCaseMutation,
  useRevertCaseMutation,
  useAutoSaveCaseMutation,

  // Queries
  useFetchAgendaQuery,
} = agendaService
export default agendaService
